import { useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { HostedAgentSecretSummary, RotateHostedAgentSecretResponse } from "@/api/admin-api"

import { AlertTriangle, Check, Copy, Key } from "@/components/icons"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Spinner } from "@/components/ui/spinner"

import { formatHostedAgentTimestamp } from "../utils/hosted-agents"

type RotateHostedAgentSecretDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  /**
   * `secret` is the durable `{configured, version, rotated_at}` summary from
   * the agent register. A6: the one-time secret lives only in `result` and is
   * dropped on close, so without this a REOPENED dialog showed neither the
   * live version nor when it was last rotated — data the API had all along.
   */
  agent: { id: string; name: string; slug: string; secret?: HostedAgentSecretSummary } | null
  /** Runs the rotation; the resolved response carries the one-time secret. */
  onRotate: () => Promise<RotateHostedAgentSecretResponse>
  isPending: boolean
}

export function RotateHostedAgentSecretDialog({
  isOpen,
  onOpenChange,
  agent,
  onRotate,
  isPending,
}: RotateHostedAgentSecretDialogProps) {
  const { t, i18n } = useTranslation()
  const [result, setResult] = useState<RotateHostedAgentSecretResponse | null>(null)
  const [copied, setCopied] = useState(false)

  if (!agent) return null

  // Prefer what this rotation just returned; fall back to the register's
  // durable summary so a reopened dialog still names the live version.
  const version = result?.secret_version ?? (agent.secret?.configured ? agent.secret.version : null)
  const rotatedAt =
    result?.rotated_at ?? (agent.secret?.configured ? agent.secret.rotated_at : null)
  const versionInfo =
    version !== null
      ? t("adminDashboard.hostedAgents.rotateDialog.versionInfo", {
          defaultValue: "Secret version {{version}}, rotated {{rotatedAt}}.",
          version,
          rotatedAt: rotatedAt
            ? (formatHostedAgentTimestamp(rotatedAt, i18n.language) ?? rotatedAt)
            : t("adminDashboard.hostedAgents.rotateDialog.rotatedAtUnknown", {
                defaultValue: "at an unknown time",
              }),
        })
      : null

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // The secret exists only in this dialog — dropping it here is final.
      setResult(null)
      setCopied(false)
    }
    onOpenChange(open)
  }

  const handleRotate = async () => {
    try {
      const response = await onRotate()
      setResult(response)
    } catch {
      // Error toast raised by the caller; keep the dialog open for retry.
    }
  }

  const handleCopy = async () => {
    if (!result) return
    try {
      await navigator.clipboard.writeText(result.secret)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error(
        t("adminDashboard.hostedAgents.rotateDialog.copyFailed", {
          defaultValue: "Could not copy to clipboard",
        })
      )
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="size-5" />
            {t("adminDashboard.hostedAgents.rotateDialog.title", {
              defaultValue: "Rotate agent secret",
            })}
          </DialogTitle>
          <DialogDescription>
            {t("adminDashboard.hostedAgents.rotateDialog.description", {
              defaultValue: "Generate a new authentication secret for {{name}} ({{slug}}).",
              name: agent.name,
              slug: agent.slug,
            })}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4">
            <Alert
              className="border-foreground bg-destructive text-destructive-foreground dark:border-foreground [&>svg]:text-destructive-foreground"
              variant="destructive"
            >
              <AlertTriangle className="size-4" />
              <AlertTitle>
                {t("adminDashboard.hostedAgents.rotateDialog.oneTimeTitle", {
                  defaultValue: "Shown only once",
                })}
              </AlertTitle>
              <AlertDescription>
                {t("adminDashboard.hostedAgents.rotateDialog.oneTimeDescription", {
                  defaultValue:
                    "Copy this secret now and store it securely — it cannot be retrieved again. The previous secret is already invalid.",
                })}
              </AlertDescription>
            </Alert>
            <div className="flex items-center gap-2">
              <code className="bg-muted min-w-0 flex-1 truncate rounded-md border px-3 py-2 font-mono text-sm">
                {result.secret}
              </code>
              <Button
                size="icon"
                type="button"
                variant="outline"
                aria-label={t("adminDashboard.hostedAgents.rotateDialog.copy", {
                  defaultValue: "Copy secret",
                })}
                onClick={() => void handleCopy()}
              >
                {copied ? (
                  <Check className="text-foreground size-4" />
                ) : (
                  <Copy className="size-4" />
                )}
              </Button>
            </div>
            <p className="text-muted-foreground text-xs" data-testid="rotate-secret-version-info">
              {versionInfo}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <Alert>
              <AlertTriangle className="size-4" />
              <AlertTitle>
                {t("adminDashboard.hostedAgents.rotateDialog.warningTitle", {
                  defaultValue: "This invalidates the current secret",
                })}
              </AlertTitle>
              <AlertDescription>
                {t("adminDashboard.hostedAgents.rotateDialog.warningDescription", {
                  defaultValue:
                    "The agent container must be redeployed with the new secret before it can authenticate again. The new secret is shown exactly once.",
                })}
              </AlertDescription>
            </Alert>
            <p className="text-muted-foreground text-xs" data-testid="rotate-secret-version-info">
              {versionInfo ??
                t("adminDashboard.hostedAgents.rotateDialog.noSecretYet", {
                  defaultValue: "No secret has been generated for this agent yet.",
                })}
            </p>
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button type="button" onClick={() => handleOpenChange(false)}>
              {t("adminDashboard.hostedAgents.rotateDialog.done", { defaultValue: "Done" })}
            </Button>
          ) : (
            <>
              <Button
                disabled={isPending}
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                {t("adminDashboard.hostedAgents.rotateDialog.cancel", { defaultValue: "Cancel" })}
              </Button>
              <Button disabled={isPending} type="button" onClick={() => void handleRotate()}>
                {isPending ? (
                  <>
                    <Spinner />
                    {t("adminDashboard.hostedAgents.rotateDialog.rotating", {
                      defaultValue: "Rotating...",
                    })}
                  </>
                ) : (
                  t("adminDashboard.hostedAgents.rotateDialog.rotate", {
                    defaultValue: "Rotate secret",
                  })
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
